import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MapPinOff, Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { http, errorMessage } from "@/lib/api";
import { isValidPostcode } from "@/lib/postcode";
import { track } from "@/lib/consent";
import { WaitlistForm } from "./WaitlistForm";
import { cn } from "@/lib/utils";

const Result = ({ result }) =>
  result.eligible ? (
    <div className="flex flex-col gap-4 rounded-2xl border border-[#FF6A00]/40 bg-[#FF6A00]/10 p-5 sm:flex-row sm:items-center" data-testid="postcode-result-eligible">
      <CheckCircle2 className="h-7 w-7 shrink-0 text-[#FF6A00]" />
      <div className="flex-1">
        <p className="font-display text-lg font-semibold">Great news! We deliver to your area.</p>
        <p className="text-sm text-[#F7F5F0]/70">{result.postcode}{result.area_name ? ` · ${result.area_name}` : ""}</p>
      </div>
      <Link to="/plans" className="lc-btn-primary !py-3" data-testid="postcode-choose-plan">Choose your plan <ArrowRight className="h-4 w-4" /></Link>
    </div>
  ) : (
    <div className="rounded-2xl border border-[#2E2E2E] bg-[#111] p-5" data-testid="postcode-result-ineligible">
      <div className="flex gap-4">
        <MapPinOff className="h-7 w-7 shrink-0 text-[#D4A437]" />
        <div>
          <p className="font-display text-lg font-semibold">Sorry, we're not delivering to your postcode just yet.</p>
          <p className="text-sm text-[#A1A1AA]">Join the waiting list and we'll let you know as soon as we reach {result.postcode}.</p>
        </div>
      </div>
      <WaitlistForm postcode={result.postcode} />
    </div>
  );

export const PostcodeChecker = ({ className, idPrefix = "postcode" }) => {
  const [value, setValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!isValidPostcode(value)) {
      setError("Please enter a full UK postcode, e.g. BT39 0AA.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await http.post("/postcode/check", { postcode: value });
      if (!data.valid) setError(data.message);
      else {
        setResult(data);
        track("postcode_check", { eligible: data.eligible, outward: data.outward });
        if (data.eligible) sessionStorage.setItem("lc_postcode", data.postcode);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const inputId = `${idPrefix}-input`;
  return (
    <div className={cn("w-full", className)}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3 sm:flex-row" data-testid={`${idPrefix}-form`}>
        <label htmlFor={inputId} className="sr-only">Enter your full postcode</label>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6b6b6b]" />
          <Input
            id={inputId}
            value={value}
            onChange={(e) => setValue(e.target.value.toUpperCase())}
            placeholder="Enter your full postcode, e.g. BT39 0AA"
            autoComplete="postal-code"
            maxLength={10}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            className="lc-input h-14 rounded-full pl-12 text-base uppercase tracking-wide"
            data-testid={`${idPrefix}-input`}
          />
        </div>
        <button type="submit" disabled={loading} className="lc-btn-primary h-14 sm:min-w-[160px]" data-testid={`${idPrefix}-submit`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check postcode"}
        </button>
      </form>
      <div aria-live="polite" className="mt-4 empty:hidden">
        {error && <p id={`${inputId}-error`} className="px-2 text-sm text-[#ff8a3d]" data-testid={`${idPrefix}-error`}>{error}</p>}
        {result && <Result result={result} />}
      </div>
    </div>
  );
};
